import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { getUserRoles } from '../../utils/userRoles';
import { getEntityLabel, resolveEntityType } from '../../utils/entityLabels';
import { useRoleContext } from '../../context/RoleContext';
import { CONTEXT_TYPES, ROLE_CONTEXT_LABELS } from '../../constants/roleContext';
import { WEB_BASE_URL } from '../../config/app';
import { resolveMediaUrl } from '../../utils/media';
import { FeatureBadgeSlot } from '../../features/commercial/FeatureExperience';

const APP_MODE_STORAGE_KEY = 'appMode';


const toDisplayText = (value, fallback = '') => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (!value || typeof value !== 'object') return fallback;
  return value.name || value.title || value.label || value.email || value.entityName || value._id || value.id || fallback;
};

const getPublicTournamentPageType = (pathname = '') => {
  if (/^\/public\/tournaments\/[^/]+\/matches\/[^/]+\/?$/.test(pathname)) {
    return 'public-match-center';
  }

  if (/^\/public\/tournaments\/[^/]+\/leaderboard\/?$/.test(pathname)) {
    return 'public-tournament-leaderboard';
  }

  if (/^\/public\/tournaments\/[^/]+\//.test(pathname) || /^\/public\/tournaments\/[^/]+\/?$/.test(pathname)) {
    return 'public-tournament';
  }

  return '';
};

const StoreBadge = ({ type, icon, label }) => (
  <button type="button" className={`public-store-badge public-store-badge--${type}`} aria-label={`Download on ${label}`}>
    {/*<span className="public-store-badge__icon" aria-hidden="true">{icon}</span>*/}
    {/*<span className="public-store-badge__label">{label}</span>*/}

  </button>
);


const resolveEntityId = (value) => {
  if (!value) return '';
  return String(value._id || value.id || value.entityId || value);
};

const resolveEntityName = (value) => toDisplayText(value);

const findMembershipEntityName = (user, entityType, activeContext) => {
  const bucketByType = {
    organization: Array.isArray(user?.organizations) ? user.organizations : [],
    community: Array.isArray(user?.communities) ? user.communities : [],
    club: Array.isArray(user?.clubs) ? user.clubs : [],
  };

  const bucket = bucketByType[entityType] || [];
  const activeEntityId = activeContext?.entityType === entityType ? String(activeContext.entityId || '') : '';
  const activeMembership = activeEntityId
    ? bucket.find((entry) => resolveEntityId(entry) === activeEntityId)
    : null;
  const membership = activeMembership || bucket[0] || null;

  return resolveEntityName(membership?.entity || membership);
};

function AppHeader({ hideHeader, isLoadingAuth, message, showHero = false, user, logout }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectorState, setSelectorState] = useState(null);
  const [selectorQuery, setSelectorQuery] = useState('');
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    activeContext,
    groupedContexts,
    setActiveContext,
    resetToIndividual,
    getAvailableContexts,
    validateContext,
    getLandingPath,
  } = useRoleContext();

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen]);

  const displayName = toDisplayText(user?.name) || toDisplayText(user?.email) || 'User';
  const userRoles = getUserRoles(user);
  const isSuperAdmin = userRoles.has('super_admin') || user?.isSuperAdmin;
  const isCommunityAdmin = userRoles.has('community_admin')
    || (userRoles.has('organization_admin') && user?.community && !user?.organization);
  const roleLabelMap = {
    super_admin: 'Super admin',
    organization_admin: 'Organization admin',
    community_admin: 'Community admin',
    club_admin: 'Club admin',
    team_admin: 'Team admin',
    tournament_admin: 'Tournament admin',
    tournament_manager: 'Tournament manager',
    team_owner: 'Team owner',
    individual: 'Home',
  };
  const roleLabel = isCommunityAdmin
    ? 'Community admin'
    : roleLabelMap[user?.role] || (typeof user?.role === 'string' ? user.role.replace('_', ' ') : '') || 'User';
  const avatarSrc = user?.player?.media?.profilePhoto
    || user?.media?.profilePhoto
    || user?.avatar
    || user?.image
    || user?.photo
    || user?.avatarUrl
    || user?.profilePicture;
  const resolvedAvatarSrc = resolveMediaUrl(avatarSrc);
  const organizationName = findMembershipEntityName(user, 'organization', activeContext)
    || resolveEntityName(user?.organization);
  const communityName = findMembershipEntityName(user, 'community', activeContext)
    || resolveEntityName(user?.community);
  const clubName = findMembershipEntityName(user, 'club', activeContext)
    || resolveEntityName(user?.club);

  const activeEntityType = resolveEntityType(
    activeContext?.entityType,
    user?.activeEntity?.type,
    user?.profile?.entityType,
    user?.entityType
  );
  const entityNameByType = {
    ORGANIZATION: organizationName,
    ORG: organizationName,
    COMMUNITY: communityName,
    CLUB: clubName,
  };
  const entityTypeLabel = getEntityLabel(activeEntityType);
  const entityDisplayName = toDisplayText(
    entityNameByType[activeEntityType]
    || organizationName
    || communityName
    || clubName,
    `${entityTypeLabel} not assigned`
  );


  const roleTabs = useMemo(
    () => groupedContexts
      .filter((context) => context.type !== CONTEXT_TYPES.INDIVIDUAL)
      .map((context) => ({
        ...context,
        label: context.type === CONTEXT_TYPES.TOURNAMENT_ADMIN ? 'Tournaments' : ROLE_CONTEXT_LABELS[context.type] || toDisplayText(context.label, 'Context'),
      })),
    [groupedContexts]
  );

  const safeNavigateToContext = (nextContext) => {
    const isIndividualRouteRequest = nextContext?.type === CONTEXT_TYPES.INDIVIDUAL;
    const validContext = isIndividualRouteRequest
      ? { type: CONTEXT_TYPES.INDIVIDUAL, entityType: null, entityId: null }
      : validateContext(nextContext, getAvailableContexts());

    if (!validContext) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[redirect-debug] AppHeader.safeNavigateToContext -> invalid context, resetting to individual');
      }
      resetToIndividual();
      navigate('/dashboard/individual/home', { replace: true });
      return;
    }

    const targetPath = isIndividualRouteRequest ? '/dashboard/individual/home' : getLandingPath(validContext);
    const currentPath = `${location.pathname}${location.search}`;

    if (currentPath === targetPath) {
      return;
    }

    const isSameContext =
      activeContext?.type === validContext?.type
      && String(activeContext?.entityType || '') === String(validContext?.entityType || '')
      && String(activeContext?.entityId || '') === String(validContext?.entityId || '');

    if (!isSameContext) {
      setActiveContext(validContext);
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(APP_MODE_STORAGE_KEY, targetPath.startsWith('/sa') ? 'sa' : 'individual');
    }

    navigate(targetPath, { replace: true });
  };

  const openContext = (contextType) => {
    if (contextType === CONTEXT_TYPES.INDIVIDUAL) {
      safeNavigateToContext({ type: CONTEXT_TYPES.INDIVIDUAL, entityType: null, entityId: null });
      return;
    }

    const matching = roleTabs.find((tab) => tab.type === contextType);
    if (!matching) return;

    if (contextType === CONTEXT_TYPES.TOURNAMENT_ADMIN) {
      safeNavigateToContext({ type: CONTEXT_TYPES.TOURNAMENT_ADMIN, entityType: null, entityId: null });
      return;
    }

    if (!matching.entities?.length) {
      return;
    }

    if (matching.entities.length === 1) {
      const [single] = matching.entities;
      safeNavigateToContext(single);
      return;
    }

    setSelectorQuery('');
    setSelectorState(matching);
  };

  const filteredSelectorEntities = (selectorState?.entities || []).filter((entity) =>
    toDisplayText(entity.entityName).toLowerCase().includes(selectorQuery.toLowerCase())
  );

  const isIndividualActive = activeContext?.type === CONTEXT_TYPES.INDIVIDUAL;

  const publicTournamentPageType = getPublicTournamentPageType(location.pathname);
  const isPublicTournamentPage = Boolean(publicTournamentPageType);

  useEffect(() => {
    if (!isPublicTournamentPage) return;

    // eslint-disable-next-line no-console
    console.debug('PUBLIC_HEADER_RENDER_DEBUG', {
      pageType: publicTournamentPageType,
      logoVisible: true,
      playStoreVisible: true,
      appStoreVisible: true,
      homeVisible: false,
      loginVisible: false,
      registerVisible: false,
    });
  }, [isPublicTournamentPage, publicTournamentPageType]);

  if (hideHeader) {
    return null;
  }

  if (isPublicTournamentPage) {
    return (
      <header className="app-hero public-site-header">
        <div className="app-topbar">
          <div className="container py-3 public-site-header__inner">
            <a href={WEB_BASE_URL || '/'} className="d-inline-flex align-items-center public-site-header__logo" aria-label="CrickArena home">
              <img src="/logoNew10.svg" alt="CrickArena logo" className="app-logo" />
            </a>
            <div className="public-store-badges" aria-label="CrickArena app download links">
              <a href="#">
                <img src="/apple-store.png" alt="Apple store logo" className="app-logo" />
                <img src="/apple-icon.png" alt="Apple store logo" className="app-logo" />
              </a>
              <a href="#">
                <img src="/google-play.png" alt="Google play logo" className="app-logo" />
                <img src="/google-play-icon.png" alt="Google play logo" className="app-logo" />
              </a>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="app-hero">
      <div className="app-topbar">
        <div className="container py-3 d-flex flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between gap-3">
          <div className="d-flex align-items-center gap-3">
            <a href={`${WEB_BASE_URL}${isSuperAdmin ? '/sa/dashboard' : '/dashboard/individual/home'}`} className="d-inline-flex align-items-center beta-version">
              <img src="/logoNew10.svg" alt="CrickArena logo" className="app-logo" />
              <FeatureBadgeSlot featureKey="platform.release_status"/>
            </a>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="btn-group app-hero__tabs m-auto" role="group" aria-label="App view toggle">
              {isSuperAdmin && (
                <>
                  <NavLink
                    to="/sa/dashboard"
                    className={({ isActive }) => `btn btn-light ${isActive ? 'active' : ''}`}
                    onClick={() => window.localStorage.setItem(APP_MODE_STORAGE_KEY, 'sa')}
                    end
                  >
                    Dashboard
                  </NavLink>
                  <NavLink
                    to="/sa/organizations"
                    className={({ isActive }) => `btn btn-light ${isActive ? 'active' : ''}`}
                    onClick={() => window.localStorage.setItem(APP_MODE_STORAGE_KEY, 'sa')}
                  >
                    Organizations
                  </NavLink>
                  <NavLink
                    to="/sa/clubs"
                    className={({ isActive }) => `btn btn-light ${isActive ? 'active' : ''}`}
                    onClick={() => window.localStorage.setItem(APP_MODE_STORAGE_KEY, 'sa')}
                  >
                    Clubs
                  </NavLink>
                  <NavLink
                    to="/sa/communities"
                    className={({ isActive }) => `btn btn-light ${isActive ? 'active' : ''}`}
                    onClick={() => window.localStorage.setItem(APP_MODE_STORAGE_KEY, 'sa')}
                  >
                    Communities
                  </NavLink>
                  <NavLink
                    to="/sa/settings"
                    className={({ isActive }) => `btn btn-light ${isActive ? 'active' : ''}`}
                    onClick={() => window.localStorage.setItem(APP_MODE_STORAGE_KEY, 'sa')}
                  >
                    Settings
                  </NavLink>
                  <NavLink
                    to="/sa/dashboard/individual"
                    className={({ isActive }) => `btn btn-light ${isActive ? 'active' : ''}`}
                    onClick={() => window.localStorage.setItem(APP_MODE_STORAGE_KEY, 'sa')}
                  >
                    Home
                  </NavLink>
                </>
              )}
              {!isSuperAdmin && (
                <>
                  <button
                    type="button"
                    className={`btn btn-light ${isIndividualActive ? 'active' : ''}`}
                    onClick={() => openContext(CONTEXT_TYPES.INDIVIDUAL)}
                  >
                    Home
                  </button>
                  {roleTabs.map((tab) => (
                    <button
                      key={tab.type}
                      type="button"
                      className={`btn btn-light ${activeContext?.type === tab.type ? 'active' : ''}`}
                      onClick={() => openContext(tab.type)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex gap-3 flex-wrap align-items-center ms-lg-auto">
              {isLoadingAuth && (
                <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
              )}
              {user ? (
                <div className="dropdown app-user-dropdown" ref={menuRef}>
                  <button
                    type="button"
                    className="btn btn-light d-flex align-items-center gap-2 app-user-toggle"
                    onClick={() => setIsMenuOpen((open) => !open)}
                    aria-expanded={isMenuOpen}
                    aria-haspopup="true"
                  >
                    <span className="app-user-avatar" aria-hidden="true">
                      <img src={resolvedAvatarSrc || '/avatar.png'} alt={displayName} />
                    </span>
                  </button>
                  <div className={`dropdown-menu dropdown-menu-end shadow-sm p-3 ${isMenuOpen ? 'show' : ''}`}>
                    <p className="text-muted text-uppercase small mb-1">Signed in as</p>
                    <p className="fw-semibold mb-2">{displayName}</p>
                    <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                      <span className="badge badge-soft-primary">{toDisplayText(user.email)}</span>
                      <span className="badge badge-soft-primary text-capitalize">{roleLabel}</span>
                    </div>
                    <div className="gap-1 mb-3">
                      <p className="text-muted text-uppercase small mb-0">{entityTypeLabel}</p>
                      <span className="badge badge-soft-primary text-capitalize" title={entityDisplayName}>
                        {entityDisplayName}
                      </span>
                    </div>
                    <div className="list-group list-group-flush mb-3">
                      <NavLink
                        to="/account"
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Account
                      </NavLink>
                      <NavLink
                        to="/dashboard/individual/tournaments"
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        My tournaments
                      </NavLink>
                      <NavLink
                        to="/dashboard/individual/my-matches"
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        My matches
                      </NavLink>
                      <NavLink
                        to="/profile"
                        end
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        My profile
                      </NavLink>
                      <NavLink
                        to="/account/affiliation"
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Affiliation
                      </NavLink>
                      <NavLink
                        to="/players"
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Browse players
                      </NavLink>
                      <NavLink
                        to="/pricing"
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Pricing plan
                      </NavLink>
                      <NavLink
                        to="/guidance"
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Guidance
                      </NavLink>
                      <NavLink
                        to="/faq"
                        className="list-group-item list-group-item-action px-0"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        FAQ
                      </NavLink>
                    </div>
                    <button
                      type="button"
                      className="dropdown-item text-danger fw-semibold"
                      onClick={() => {
                        setIsMenuOpen(false);
                        logout();
                      }}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <NavLink to="/login" className="btn btn-outline-secondary btn-sm">
                    Login
                  </NavLink>
                  <NavLink to="/auth/register" className="btn btn-primary btn-sm">
                    Register
                  </NavLink>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {selectorState && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select {selectorState.label} context</h5>
                <button type="button" className="btn-close" onClick={() => setSelectorState(null)} aria-label="Close" />
              </div>
              <div className="modal-body">
                <input
                  type="search"
                  className="form-control mb-3"
                  placeholder="Search by entity name"
                  value={selectorQuery}
                  onChange={(event) => setSelectorQuery(event.target.value)}
                />
                <div className="list-group">
                  {filteredSelectorEntities.map((entity) => (
                    <button
                      key={`${entity.type}-${entity.entityType}-${entity.entityId}`}
                      type="button"
                      className="list-group-item list-group-item-action"
                      onClick={() => {
                        safeNavigateToContext(entity);
                        setSelectorState(null);
                      }}
                    >
                      {toDisplayText(entity.entityName, 'Unnamed entity')}
                    </button>
                  ))}
                  {!filteredSelectorEntities.length && (
                    <p className="text-muted small mb-0">No matching entities found.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showHero && (
        <div className="hero-header container py-4 py-lg-5 d-flex flex-column flex-lg-row justify-content-between align-items-start gap-4">
          <div className="d-flex flex-column gap-2">
            <p className="eyebrow text-white-50 mb-1">
              Player profile builder <FeatureBadgeSlot featureKey="player.profile"/>
            </p>

            <h1 className="app-hero__title mb-2 text-white">
              CrickArena player profile
            </h1>

            <p className="lead text-light mb-2">
              Capture the essentials for new cricketers—personal info, skills, stats—with more advanced features rolling out soon.
            </p>

          </div>
        </div>
      )}
    </header>
  );
}

export default AppHeader;
